import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { find, get, has, invoke, isEmpty } from 'lodash';
import { css } from '@emotion/css';
import SidePanel from '../../common/SidePanel/SidePanel';
import SubstitutionsList from './SubstitutionsList';
import { SessionContext, SessionContextType } from '../LandingPage/SessioncContext';
import { useApp } from '../../context/app-context';
import getSubstitutions from '../../service/get-substitutions';
import updateItemInfo from '../../service/update-item-info-service';
import { ItemsContext, ItemsContextType } from './ItemsContext';
import { Spinner } from '../../common/Spinner/Spinner';

const linkStyle = css`
  color: #7b1a27;
  cursor: pointer;
  border: 0;
  background: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 380px) {
    white-space: nowrap;
    font-size: 0.8rem;
  }
`;

const blackLinkStyle = css`
  color: #1f1e1e !important;
  padding-left: 1rem;
`;

const panelContentStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 1rem;
  overflow: hidden;
`;

const substitutionLinkStyle = css`
  padding: 2rem;
  display: flex;
  align-content: center;
  justify-content: center;

  & button {
    border: 0;
    background: #fff;
    color: #7b1a27;
    font-size: 1rem;
    text-decoration: underline;
    cursor: pointer;
  }
`;

const spinnerContainerStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 90;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgb(240 240 240 / 60%);
`;

const doNotSubstituteOutOfStockMessageContainerStyle = css`
  display: flex;
  align-content: center;
  align-items: center;
  margin-top: 2rem;
`;

const doNotSubstituteOutOfStockTextStyle = css`
  margin-bottom: 0;
  margin-left: 0.5rem;
`;

export type SubstitutionsContextType = {
  loadSubstitutions: () => void;
  clearSubstitutions: () => void;
  substitutionData: object[];
  searchToken: string;
  setSearchToken: (t: string) => void;
  postSelectedProduct: (prod: object) => void;
  postDoNotSubstitute: () => void;
};
export const SubstitutionsContext = createContext<SubstitutionsContextType | null>(null);

const SubstitutionIcon = () => (
  <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M.831 3.77a.48.48 0 0 1 .53-.45c2.41.215 3.871.745 5 1.932a.492.492 0 0 1-.01.702.509.509 0 0 1-.712-.035C4.767 5 3.61 4.516 1.306 4.309a.521.521 0 0 1-.475-.54Z"
      fill="#7B1A27"
    />
    <path
      d="M12.05 13.944a.5.5 0 0 0 .694.138l2.37-1.58a.5.5 0 0 0 0-.832l-2.37-1.581a.5.5 0 1 0-.555.832l.973.649h-1.145c-1.108 0-1.677-.134-2.036-.329-.348-.189-.562-.467-.897-.97a.516.516 0 0 0-.699-.16.475.475 0 0 0-.221.413c.001.093.03.187.086.272.33.497.669.985 1.271 1.313.593.321 1.375.455 2.533.455h1.165l-1.03.687a.5.5 0 0 0-.138.693ZM12.744 5.809a.5.5 0 0 1-.555-.832l1.028-.686h-1.2c-1.472 0-2.222.43-2.713 1.013-.46.544-.727 1.248-1.049 2.095l-.174.456c-.393 1.012-.895 2.135-1.913 3.046-1.027.92-2.52 1.572-4.807 1.776a.48.48 0 0 1-.53-.45.521.521 0 0 1 .475-.54c2.13-.19 3.388-.784 4.202-1.511.822-.736 1.252-1.66 1.635-2.647l.16-.418c.315-.837.653-1.734 1.238-2.427.732-.868 1.805-1.387 3.513-1.387h1.11l-.975-.65a.5.5 0 1 1 .555-.832l2.37 1.58a.5.5 0 0 1 0 .833l-2.37 1.58Z"
      fill="#7B1A27"
    />
  </svg>
);

const SubstitutionIconBlack = () => (
  <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.7921 13.3008C12.5624 13.454 12.252 13.392 12.0987 13.1623C11.9455 12.9326 12.0074 12.6222 12.2371 12.4689L13.3842 11.7036H12.0807C10.8671 11.7036 10.0525 11.5638 9.43671 11.2293C8.81057 10.8892 8.45921 10.382 8.11189 9.86102C7.95656 9.62803 8.01733 9.3215 8.24762 9.17636C8.47791 9.03123 8.79052 9.10245 8.94585 9.33544C9.29779 9.86335 9.52475 10.1603 9.89635 10.3621C10.2783 10.5696 10.8808 10.7095 12.0442 10.7095L13.3242 10.7095L12.2371 9.98421C12.0074 9.83095 11.9455 9.52049 12.0987 9.29078C12.252 9.06108 12.5624 8.99911 12.7921 9.15237L15.2775 10.8106C15.4165 10.9034 15.5 11.0595 15.5 11.2266C15.5 11.3937 15.4165 11.5497 15.2775 11.6425L12.7921 13.3008Z"
      fill="#1F1E1E"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.33474 2.50612C0.349783 2.23275 0.58683 2.03124 0.8642 2.05604C3.39054 2.28193 4.9154 2.83682 6.0925 4.07522C6.28608 4.27889 6.28153 4.59319 6.08232 4.77722C5.88311 4.96125 5.56468 4.94533 5.37109 4.74166C4.45041 3.77302 3.2284 3.26228 0.809726 3.04602C0.532356 3.02122 0.319698 2.7795 0.33474 2.50612Z"
      fill="#1F1E1E"
    />
    <path
      d="M12.7921 4.62204C12.5624 4.7753 12.252 4.71334 12.0987 4.48363C11.9455 4.25392 12.0074 3.94346 12.2371 3.7902L13.3816 3.0266L12.0441 3.0266C10.4943 3.0266 9.69923 3.48049 9.17848 4.09779C8.69376 4.67238 8.41146 5.41558 8.07418 6.30355C8.01535 6.45844 7.95484 6.61773 7.89129 6.78131C7.47946 7.84141 6.95536 9.01513 5.89172 9.96656C4.81941 10.9258 3.2587 11.6087 0.864196 11.8228C0.586826 11.8476 0.349778 11.6461 0.334736 11.3727C0.319693 11.0993 0.532351 10.8576 0.809721 10.8328C3.04801 10.6327 4.37297 10.0087 5.23169 9.24058C6.0991 8.46468 6.5514 7.49062 6.95383 6.45473C7.00972 6.31086 7.0651 6.16401 7.12111 6.0155C7.45231 5.13731 7.80546 4.2009 8.41546 3.47782C9.17715 2.5749 10.2949 2.03256 12.0806 2.03256L13.3268 2.03256L12.2371 1.3055C12.0074 1.15224 11.9455 0.841777 12.0987 0.612071C12.252 0.382365 12.5624 0.320396 12.7921 0.47366L15.2775 2.13193C15.4165 2.22468 15.5 2.38074 15.5 2.54785C15.5 2.71496 15.4165 2.87102 15.2775 2.96377L12.7921 4.62204Z"
      fill="#1F1E1E"
    />
  </svg>
);

const EditSubstitutionLink = (props) => {
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const currentItemsContext = useContext(ItemsContext) as ItemsContextType;
  const user = get(currentSessionContext, 'user');
  const { appData, showToast } = useApp();
  const className = get(props, 'className');
  const showSubstitutionButton = get(props, 'showSubstitutionButton');
  const item = get(props, 'item');
  const [loading, setLoading] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [substitutionData, setSubstitutionData] = useState<object[]>([]);
  const [firstLoadSubstitutionData, setFirstLoadSubstitutionData] = useState<object[]>([]);
  const [searchToken, setSearchToken] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<object | undefined>(undefined);
  const [choosingSubstitution, setChoosingSubstitution] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [applyingChange, setApplyingChange] = useState(false);

  const isSubstitutable = get(item, 'isSubstitutable', false);
  const userChoseToNotSubstitute = get(item, 'substitutionValue') === 'Do Not Substitute' && isSubstitutable;
  const showSetSubstitution = get(item, 'substitutionValue') === 'Best Available' && !isSubstitutable;

  const [shouldShowInitialResults, setShouldShowInitialResults] = useState(false);
  const [sendAnalytics, setSendAnalytics] = useState(false);

  useEffect(() => {
    const selected = find(get(substitutionData, 'substituteItem'), {
      itemIsSelected: true,
    });
    if (has(selected, 'itemBpn') && get(selectedProduct, 'itemBpn') !== get(selected, 'itemBpn')) {
      setSelectedProduct(selected);
    }

    setShouldShowInitialResults(isEmpty(searchToken));
  }, [substitutionData]);

  useEffect(() => {
    if (isEmpty(searchToken)) {
      setShouldShowInitialResults(true);
    }
  }, [searchToken]);

  useEffect(() => {
    if (userChoseToNotSubstitute) {
      loadSubstitutions();
    }
  }, [choosingSubstitution]);

  const fetchSubstitutions = async (searchToken: string): Promise<void> => {
    const substitutions = await invoke(currentSessionContext, 'ensureActiveSession', () =>
      getSubstitutions(appData.environmentVariables, user, get(item, 'productBpn'), searchToken, appData.banner, null),
    );
    setSubstitutionData(get(substitutions, 'data'));
    setmodalEvents(searchToken ? 'search' : 'view', get(substitutions, 'data'));
    if (isEmpty(firstLoadSubstitutionData)) {
      setFirstLoadSubstitutionData(get(substitutions, 'data'));
    }
  };

  const setmodalEvents = (action: string, subData?: any, selectedProduct?: any) => {
    try {
      if (window['AB']?.['DATALAYER']?.setModalViewEventsSnsSubs) {
        const analyticsSubstitutionObj = {
          productID: get(item, 'productBpn'),
          searchTerm: searchToken,
          subData: subData ? subData : substitutionData,
          showSetSubstitution: showSetSubstitution,
          selectedProduct: selectedProduct,
        };
        if (action === 'view') {
          if (sendAnalytics) {
            window['AB']['DATALAYER'].setModalViewEventsSnsSubs(analyticsSubstitutionObj);
            setSendAnalytics(false);
          }
        } else {
          window['AB']['DATALAYER'].setModalClickEventsSnsSubs(action, analyticsSubstitutionObj);
        }
      }
    } catch (error) {
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const analyticsErrors = (message: string) => {
    try {
      if (window['AB']?.['DATALAYER']?.setModalViewEventsSnsSubs) {
        window['AB']['DATALAYER'].setError('no-value', 'substitution|error', message);
      }
    } catch (error) {
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const loadSubstitutions = async (): Promise<void> => {
    setLoading(true);
    setApiError(false);
    setSearchError(false);

    try {
      if (!userChoseToNotSubstitute || choosingSubstitution) {
        await fetchSubstitutions(searchToken);
      } else {
        setmodalEvents('view');
      }
    } catch (error) {
      if (isEmpty(searchToken)) {
        setApiError(true);
      } else {
        setSearchError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const postSelectedProduct = async (selectedProduct: object) => {
    setApplyingChange(true);
    try {
      await invoke(currentSessionContext, 'ensureActiveSession', () =>
        updateItemInfo(appData.environmentVariables, user, {
          subscriptionId: get(item, 'subscriptionId'),
          isSubstitutable: true,
          substituteItem: [selectedProduct],
          itemBpn: get(item, 'productBpn'),
          isReplinishItem: get(item, 'isReplinishItem', true), // The default value is set to true because in scheduled items tab we only show replenish items and isReplinishItem is not available scheduled items scope
        }),
      );

      await invoke(currentItemsContext, 'fetchItemsInfo');

      showToast('info', 'Your substitution has been updated.');
      setmodalEvents('substitute', null, selectedProduct);
    } catch (e) {
      showToast('error', "We couldn't complete your substitution request. Please try again later.");
      analyticsErrors("We couldn't complete your substitution request. Please try again later.");
    }
  };

  const postDoNotSubstitute = async () => {
    setApplyingChange(true);
    try {
      await invoke(currentSessionContext, 'ensureActiveSession', () =>
        updateItemInfo(appData.environmentVariables, user, {
          subscriptionId: get(item, 'subscriptionId'),
          isSubstitutable: true,
          itemBpn: get(item, 'productBpn'),
          isReplinishItem: get(item, 'isReplinishItem', true), // The default value is set to true because in scheduled items tab we only show replenish items and isReplinishItem is not available scheduled items scope
        }),
      );

      await invoke(currentItemsContext, 'fetchItemsInfo');

      showToast('info', 'Your item will not be substituted.');
      setmodalEvents('dont-substitute');
    } catch (e) {
      showToast('error', "We couldn't complete your substitution request. Please try again later.");
      analyticsErrors("We couldn't complete your substitution request. Please try again later.");
    }
  };

  const clearSubstitutions = () => {
    setSubstitutionData([]);
  };

  const handleClick = (evt) => {
    evt.preventDefault();
    evt.stopPropagation();

    setShowPanel(true);
    setSendAnalytics(true);
  };

  const substitutionItems =
    shouldShowInitialResults && isEmpty(get(substitutionData, 'substituteItem'))
      ? get(firstLoadSubstitutionData, 'substituteItem')
      : get(substitutionData, 'substituteItem');
  const showSubstituionIcon = () => (showSubstitutionButton ? <SubstitutionIconBlack /> : <SubstitutionIcon />);

  return (
    <div className={className}>
      <button
        className={`${showSubstitutionButton && blackLinkStyle} ${linkStyle} link-sm`}
        onClick={handleClick}
        aria-label="edit substitution"
        disabled={loading}
      >
        {showSubstituionIcon()}
        {showSetSubstitution ? <span>Select substitution</span> : <span>Edit substitution</span>}
      </button>

      {showPanel ? (
        <SubstitutionsContext.Provider
          value={{
            loadSubstitutions,
            clearSubstitutions,
            substitutionData,
            searchToken,
            setSearchToken,
            postSelectedProduct,
            postDoNotSubstitute,
          }}
        >
          <SidePanel
            title="Substitution preferences"
            onClose={() => {
              setShowPanel(false);
              setSearchToken('');
              setSelectedProduct(undefined);
              setChoosingSubstitution(false);
              setApplyingChange(false);
            }}
            onExpand={async () => {
              await loadSubstitutions();
            }}
            show={showPanel}
            headerStyles={css`
              padding: 1.5rem 1rem 0;
            `}
          >
            {applyingChange ? (
              <div className={spinnerContainerStyle}>
                <Spinner size={60} dots={8} dotSize={6} />
              </div>
            ) : null}
            {userChoseToNotSubstitute && !choosingSubstitution ? (
              <div className={panelContentStyle}>
                <div className={doNotSubstituteOutOfStockMessageContainerStyle}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M1.24724 5.65449C1.2698 5.24443 1.62538 4.94217 2.04143 4.97937C5.65806 5.30274 7.84932 6.09783 9.54163 7.87827C9.83201 8.18378 9.82517 8.65522 9.52635 8.93127C9.22754 9.20731 8.7499 9.18344 8.45952 8.87793C7.15183 7.50213 5.41485 6.77327 1.95972 6.46434C1.54366 6.42713 1.22468 6.06456 1.24724 5.65449Z"
                      fill="#1F1E1E"
                    />
                    <path
                      d="M18.0767 20.9162C18.3066 21.2607 18.7722 21.3537 19.1168 21.1238L22.6711 18.7523C22.8796 18.6132 23.0048 18.3791 23.0048 18.1285C23.0048 17.8778 22.8796 17.6437 22.6711 17.5046L19.1168 15.1331C18.7722 14.9032 18.3066 14.9962 18.0767 15.3407C17.8468 15.6853 17.9397 16.151 18.2843 16.3809L19.7434 17.3544H18.0265C16.3645 17.3544 15.5103 17.1543 14.9723 16.8621C14.4497 16.5783 14.1295 16.161 13.626 15.4058C13.3931 15.0563 12.9241 14.9494 12.5787 15.1671C12.3591 15.3056 12.2423 15.5418 12.2462 15.7854C12.2484 15.9249 12.2902 16.0669 12.3751 16.1942C12.8716 16.939 13.3785 17.6717 14.2828 18.1629C15.1716 18.6457 16.344 18.8456 18.0812 18.8456H19.8287L18.2843 19.876C17.9397 20.1059 17.8468 20.5716 18.0767 20.9162Z"
                      fill="#1F1E1E"
                    />
                    <path
                      d="M19.1169 8.71295C18.7723 8.94284 18.3066 8.84989 18.0767 8.50533C17.8468 8.16077 17.9398 7.69508 18.2843 7.46519L19.8256 6.43686L18.0264 6.43686C15.8183 6.43686 14.6926 7.08286 13.9563 7.95566C13.2674 8.77222 12.8662 9.82838 12.3836 11.0985C12.2994 11.3202 12.2127 11.5485 12.1216 11.783C11.5323 13.2999 10.7803 14.9854 9.25235 16.3522C7.71139 17.7306 5.4712 18.7093 2.04141 19.016C1.62536 19.0532 1.26978 18.7509 1.24722 18.3408C1.22466 17.9308 1.54364 17.5682 1.9597 17.531C5.15516 17.2453 7.04172 16.355 8.2623 15.2632C9.4959 14.1597 10.1402 12.7737 10.7154 11.2932C10.7951 11.0881 10.8741 10.8785 10.9541 10.6663C11.4275 9.4108 11.935 8.06501 12.8117 7.02571C13.9094 5.72448 15.5191 4.9458 18.0812 4.94579L19.7468 4.94579L18.2843 3.97003C17.9398 3.74013 17.8468 3.27445 18.0767 2.92989C18.3066 2.58533 18.7723 2.49237 19.1169 2.72227L22.6711 5.09373C22.8796 5.23285 23.0049 5.46694 23.0049 5.71761C23.0049 5.96827 22.8796 6.20236 22.6711 6.34149L19.1169 8.71295Z"
                      fill="#1F1E1E"
                    />
                  </svg>

                  <p className={doNotSubstituteOutOfStockTextStyle}>
                    <span>
                      if item is out of stock, <strong>don’t substitute</strong>
                    </span>
                  </p>
                </div>

                <p className={substitutionLinkStyle}>
                  <button
                    onClick={() => {
                      setChoosingSubstitution(true);
                    }}
                  >
                    <span>Choose substitution</span>
                  </button>
                </p>
              </div>
            ) : (
              <div className={panelContentStyle}>
                {loading ? (
                  <SubstitutionsList
                    product={item}
                    loading={true}
                    substitutions={[
                      {
                        itemUpc: '850351100',
                        itemBpn: '960351132',
                        itemDescription: 'Loading product 1',
                        itemPrice: 2.79,
                      },
                      {
                        itemUpc: '021130070022',
                        itemBpn: '136010121',
                        itemDescription: 'Loading product 2',
                        itemPrice: 4.29,
                      },
                      {
                        itemUpc: '021130070022',
                        itemBpn: '138350070',
                        itemDescription: 'Loading product 3',
                        itemPrice: 8.89,
                      },
                    ]}
                  />
                ) : (
                  <SubstitutionsList
                    product={item}
                    isBestAvailable={showSetSubstitution}
                    selectedProduct={selectedProduct}
                    substitutions={substitutionItems}
                    apiError={apiError}
                    searchError={searchError}
                  />
                )}
              </div>
            )}
          </SidePanel>
        </SubstitutionsContext.Provider>
      ) : null}
    </div>
  );
};

EditSubstitutionLink.propTypes = {
  text: PropTypes.string,
  item: PropTypes.object.isRequired,
  children: PropTypes.any,
  className: PropTypes.any,
  showSetSubstitution: PropTypes.string,
  showSubstitutionButton: PropTypes.bool,
};

export default EditSubstitutionLink;
